import { Field, Input, Select } from "@dl/web-core-js/ui";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { ServiceMotiveDTO, ServiceOrderLineDTO } from "../../api/servicedesk";
import type { PickedPart } from "./ServicePartsPicker";

/** What applies to every part added in one go: why, which run it came from, and a note. */
export interface LineDetails {
  motiveId: string;
  run: string;
  note: string;
}

const text = (value: string) => (value.trim() === "" ? null : value.trim());

/** The line a picked part becomes. */
export function lineFrom(part: PickedPart, orderId: number, position: number, details: LineDetails): ServiceOrderLineDTO {
  return {
    id: 0,
    service_order_id: orderId,
    position,
    part_id: part.part_id,
    quantity: Number(part.quantity),
    quantity_cancelled: 0,
    service_motive_id: Number(details.motiveId),
    product_sku: part.sku,
    production_reference: text(details.run),
    is_made_to_order: false,
    requested_from_plant_date: null,
    made_date: null,
    ready_date: null,
    note: text(details.note),
  };
}

/**
 * Why the parts cannot be added yet, or null when they can. Said beside the
 * button, since the motive sits below the fold of a long bill of materials.
 */
export function missingForLines(t: TFunction, chosen: PickedPart[], details: LineDetails): string | null {
  if (chosen.length === 0) return null;
  if (!chosen.every((part) => Number(part.quantity) > 0)) return t("sd.line.quantity-invalid", "Enter a quantity above zero.");
  if (details.motiveId === "") return t("sd.line.motive-required", "Choose a motive.");
  return null;
}

export function LineDetailsFields({
  motives,
  value,
  onChange,
}: {
  motives: ServiceMotiveDTO[];
  value: LineDetails;
  onChange: (value: LineDetails) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <Field label={t("column.sd.motive", "Motive")} htmlFor="sd-add-motive" required>
        <Select
          id="sd-add-motive"
          value={value.motiveId}
          placeholder={t("form.choose", "Choose…")}
          options={motives.map((motive) => ({ value: String(motive.id), label: `${motive.code} · ${motive.name}` }))}
          onChange={(event) => onChange({ ...value, motiveId: event.target.value })}
        />
      </Field>
      <Field label={t("column.sd.run", "Production run")} htmlFor="sd-add-run">
        <Input
          id="sd-add-run"
          value={value.run}
          placeholder={t("sd.line.run", "Run on the box label")}
          onChange={(event) => onChange({ ...value, run: event.target.value })}
        />
      </Field>
      <Field label={t("column.note", "Note")} htmlFor="sd-add-note">
        <Input id="sd-add-note" value={value.note} onChange={(event) => onChange({ ...value, note: event.target.value })} />
      </Field>
    </div>
  );
}
